import { FlowNodeInputTypeEnum } from "@packages/flow-core/module/node/constants";
import TextInput from "./templates/TextInput";
import NumberInput from "./templates/NumberInput";
import { FlowNodeInputItemType } from "@packages/flow-core/module/node/types";
import { MemoExoticComponent, memo, useCallback, useMemo } from "react";
import Label from "./Label";
import Textarea from "./templates/Textarea";
import styles from "./index.less";
import { Immutable } from "immer";
import Select from "./templates/Select";
import { JSX } from "react";
import { RenderInputProps } from "./types";
import Slider from "./templates/Slider";
import AddInputParams from "./templates/AddInputParam";
// import SelectKnowledge from "./templates/SelectKnowledge";
import AddKv from "./templates/AddKv";
import SelectAIModel from "./templates/SelectAIModel";
import Cascader from "./templates/Cascader";
import SelectedMaterial from "./templates/SelectedMaterial";
import ConditionInput from "./templates/ConditionInput";
import Switch from "./templates/Switch";
import { useFlow } from "../../FlowEdit/useFlow";
// import SelectSmartKnowledge from "./templates/SelectSmartKnowledge";
import SelectAdvanceKnowledge from "./templates/SelectAdvanceKnowledge";

const renderMap: Record<`${FlowNodeInputTypeEnum}`, any> = {
  [FlowNodeInputTypeEnum.input]: memo(TextInput),
  [FlowNodeInputTypeEnum.systemInput]: undefined,
  [FlowNodeInputTypeEnum.numberInput]: memo(NumberInput),
  [FlowNodeInputTypeEnum.select]: memo(Select),
  [FlowNodeInputTypeEnum.slider]: memo(Slider),
  [FlowNodeInputTypeEnum.target]: undefined,
  [FlowNodeInputTypeEnum.switch]: memo(Switch),
  [FlowNodeInputTypeEnum.textarea]: memo(Textarea),
  [FlowNodeInputTypeEnum.addInputParam]: memo(AddInputParams),
  [FlowNodeInputTypeEnum.aiSettings]: undefined,
  [FlowNodeInputTypeEnum.selectLLMModel]: undefined,
  [FlowNodeInputTypeEnum.selectedKnowledgeBase]: undefined, // 知识库不允许选择
  [FlowNodeInputTypeEnum.selectedAdvanceKnowledge]: memo(
    SelectAdvanceKnowledge
  ), // 迁移高级知识库
  [FlowNodeInputTypeEnum.selectedSmartKnowledge]: undefined, // 智能知识库不允许选择
  [FlowNodeInputTypeEnum.hidden]: undefined,
  [FlowNodeInputTypeEnum.custom]: undefined,
  [FlowNodeInputTypeEnum.addKv]: memo(AddKv),
  [FlowNodeInputTypeEnum.selectAiModel]: memo(SelectAIModel),
  [FlowNodeInputTypeEnum.cascader]: memo(Cascader),
  [FlowNodeInputTypeEnum.selectedMaterial]: memo(SelectedMaterial), // 产品说跟玄凤保持一致，不用管兼容问题

  // 渲染判断条件
  [FlowNodeInputTypeEnum.conditionInput]: memo(ConditionInput),
  [FlowNodeInputTypeEnum.selectWxUser]: undefined,
  [FlowNodeInputTypeEnum.selectedKnowledgeAll]: undefined,
  [FlowNodeInputTypeEnum.selectAppName]: undefined,
  [FlowNodeInputTypeEnum.uploadMaterial]: undefined,
  [FlowNodeInputTypeEnum.selectDamselfishTags]: undefined,
  [FlowNodeInputTypeEnum.selectMcp]: undefined,
};

export type Props = {
  moduleId: string;
  flowInputList: Immutable<FlowNodeInputItemType[]>;
  CustomComponent?: Record<
    string,
    | ((p: RenderInputProps) => JSX.Element | null)
    | MemoExoticComponent<(p: RenderInputProps) => JSX.Element | null>
  >;
  fieldProps?: Record<string, any>;
};

export default function RenderInput(props: Props) {
  const { flowInputList, moduleId, CustomComponent, fieldProps } = props;
  const { mode } = useFlow();

  const sortInputs = useMemo(() => {
    let isCaseConditionInputs: Immutable<FlowNodeInputItemType>[][] = [];
    const isNotCaseConditionInputs: Immutable<FlowNodeInputItemType>[] = [];

    // case 分类
    flowInputList.forEach((input) => {
      const regexpResult = /case\-([0-9]+)\-/.exec(input.key);
      if (!regexpResult) {
        isNotCaseConditionInputs.push(input);
      } else {
        const caseNumber = Number(regexpResult[1]);
        if (!isCaseConditionInputs[caseNumber]) {
          isCaseConditionInputs.push([input]);
        } else {
          isCaseConditionInputs[caseNumber].push(input);
        }
      }
    });

    isCaseConditionInputs = isCaseConditionInputs.map((currentCase) => {
      const relationInput = currentCase.filter((c) =>
        /case\-[0-9]+\-relationship/.test(c.key)
      )?.[0];
      const conditionInputs = currentCase
        .filter((c) => /case\-[0-9]+\-condition\-/.test(c.key))
        .sort((a, b) => {
          const aKeyIndex = /case\-[0-9]+\-condition\-([0-9]+)/.exec(a.key);
          const bKeyIndex = /case\-[0-9]+\-condition\-([0-9]+)/.exec(b.key);
          return Number(aKeyIndex) > Number(bKeyIndex) ? -1 : 1;
        });

      return relationInput
        ? [...conditionInputs, relationInput]
        : conditionInputs;
    }) as Immutable<FlowNodeInputItemType>[][];

    const flowInputs = [
      ...isNotCaseConditionInputs,
      ...isCaseConditionInputs.flat(Infinity),
    ] as Immutable<FlowNodeInputItemType>[];

    return flowInputs.sort((a, b) => {
      if (a.type === FlowNodeInputTypeEnum.addInputParam) {
        return 1;
      }
      if (b.type === FlowNodeInputTypeEnum.addInputParam) {
        return -1;
      }

      return 0;
    });
  }, [flowInputList]);

  const filterInputs = useMemo(
    () =>
      sortInputs.filter((input) => {
        if (mode === "app" && input.hideInApp) return false;
        if (mode === "plugin" && input.hideInPlugin) return false;

        return true;
      }),
    [sortInputs]
  );

  const isNeededToIgnoreLabel = useCallback(
    (input: Immutable<FlowNodeInputItemType>) => {
      return input.type === FlowNodeInputTypeEnum.addInputParam;
    },
    []
  );

  return filterInputs.map((input) => {
    const RenderComponent =
      input.type === "custom"
        ? CustomComponent?.[input.key]
        : renderMap[input.type];

    return input.type !== FlowNodeInputTypeEnum.hidden ? (
      <div key={input.key} className={styles.item}>
        {!!input.label && !isNeededToIgnoreLabel(input) && (
          <Label moduleId={moduleId} inputKey={input.key} {...input} />
        )}
        <div className={styles.value}>
          {RenderComponent && (
            <div className="nodrag nowheel">
              <RenderComponent
                {...fieldProps}
                inputs={filterInputs as FlowNodeInputItemType[]}
                item={input as FlowNodeInputItemType}
                moduleId={moduleId}
              />
            </div>
          )}
        </div>
      </div>
    ) : null;
  });
}
